import { Auth0Provider, Auth0ProviderOptions } from '@auth0/auth0-react';
import { initializeIcons } from '@fluentui/react';
import { registerIcons } from '@fluentui/react/lib/Styling';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { excludeGraphQLFetch } from 'apollo-link-sentry';
import { App } from 'app';
import IncognitoIcon from 'assets/svgs/incognito_filled.svg?react';
import { OpenApp } from 'common/components/OpenApp';
import { SentryBoundary } from 'error/SentryBoundary';
import { LoginWrapper } from 'login';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Route, useHistory } from 'react-router-dom';
import FluentUITheme from 'theme';
import { QueryParamProvider } from 'use-query-params';
import { ApolloWrapper } from 'utility/ApolloWrapper';
import { restorePath } from 'utility/redirect';
import StampIcon from './assets/svgs/ApproveStatusIcons/Stamp.svg?react';
import FullView from './assets/svgs/FullView.svg?react';
import ChatHelp from './assets/svgs/chat_help.svg?react';
import { loadConfig } from './config';
import './index.css';

initializeIcons();
registerIcons({
  icons: {
    Stamp: <StampIcon style={{ width: 25, height: 30 }} />,
    StampSmall: <StampIcon style={{ width: 20, height: 18 }} />,
    FullView: <FullView style={{ width: 15, height: 15 }} />,
    ChatHelp: <ChatHelp style={{ width: 24, height: 24}} />,
  },
});
registerIcons({
  icons: {
    Incognito: <IncognitoIcon style={{ width: 25, height: 30 }} />,
    IncognitoSmall: <IncognitoIcon style={{ width: 20, height: 18 }} />,
  },
});

loadConfig().then((config) => {
  if (!config) throw new Error('Configuration file not found');

  if (config.sentry) {
    Sentry.init({
      dsn: config.sentry.dsn,
      environment: config.sentry.env,
      integrations: [new BrowserTracing()],
      tracesSampleRate: config.sentry.sampleRate ?? 0,
      beforeBreadcrumb: excludeGraphQLFetch,
      release: import.meta.env.VITE_BUILD_VERSION,
    });
  }

  const Auth0ProviderWithRedirect: React.FC<Auth0ProviderOptions> = (props) => {
    const history = useHistory();
    return (
      <Auth0Provider
        {...props}
        onRedirectCallback={(appState) => {
          if (appState) {
            const path = restorePath(appState);
            if (path) history.replace(path);
          }
        }}
      />
    );
  };
  const rootElement = document.getElementById('root');
  if (rootElement) {
    const root = createRoot(rootElement);

    root.render(
      <OpenApp>
        <BrowserRouter>
          <QueryParamProvider ReactRouterRoute={Route}>
            <Auth0ProviderWithRedirect
              domain={config.auth0.domain}
              clientId={config.auth0.clientId}
              redirectUri={window.location.origin}
              audience={config.auth0.audience}
            >
              <ApolloWrapper>
                <FluentUITheme>
                  <Toaster
                    position="bottom-left"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#333',
                        color: '#fff',
                        borderRadius: '8px',
                        padding: '16px',
                      },
                      success: {
                        icon: '✅',
                        style: {
                          background: 'green',
                        },
                      },
                      error: {
                        icon: '❌',
                        style: {
                          background: 'crimson',
                        },
                      },
                    }}
                  />
                  <SentryBoundary>
                    <LoginWrapper>
                      <App />
                    </LoginWrapper>
                  </SentryBoundary>
                </FluentUITheme>
              </ApolloWrapper>
            </Auth0ProviderWithRedirect>
          </QueryParamProvider>
        </BrowserRouter>
      </OpenApp>
    );
  }
});
